import { useState, useRef, useEffect } from 'react';
import { useBookAvatars } from '@/hooks/use-book-avatars';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Loader2, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';

interface BookAvatarSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (avatarUrl: string) => void;
  currentAvatar?: string | null;
}

interface AvatarGroupProps {
  group: { bookTitle: string; avatars: Array<{ id: string; image_url: string }> };
  selectedAvatar: string | null;
  onSelect: (avatarUrl: string) => void;
}

const AvatarGroup = ({ group, selectedAvatar, onSelect }: AvatarGroupProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    const scrollElement = scrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener('scroll', checkScroll);
      return () => scrollElement.removeEventListener('scroll', checkScroll);
    }
  }, [group.avatars]);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 300;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="space-y-3">
      <h3 className="text-base md:text-lg font-medium px-2 md:px-4 font-apple text-white">
        {group.bookTitle}
      </h3>
      
      <div className="relative group/scroll">
        {canScrollLeft && (
          <button
            onClick={() => scroll('left')}
            className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-background/90 backdrop-blur-sm rounded-full items-center justify-center shadow-lg hover:bg-background transition-all"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}
        
        <div
          ref={scrollRef}
          className="flex gap-3 overflow-x-auto snap-x snap-mandatory scrollbar-hide px-2 md:px-4 pb-2"
        >
          {group.avatars.map((avatar) => (
            <button
              key={avatar.id}
              onClick={() => onSelect(avatar.image_url)}
              className={cn(
                "relative flex-shrink-0 w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden transition-all snap-start",
                "ring-2 ring-transparent active:scale-95",
                selectedAvatar === avatar.image_url ? "ring-primary ring-3 scale-105" : "hover:scale-105"
              )}
            >
              <img
                src={avatar.image_url}
                alt={`${group.bookTitle} character`}
                className="w-full h-full object-cover"
              />
              {selectedAvatar === avatar.image_url && (
                <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                  <div className="bg-primary rounded-full p-1.5">
                    <Check className="w-3 h-3 md:w-4 md:h-4 text-primary-foreground" />
                  </div>
                </div>
              )}
            </button>
          ))}
        </div>

        {canScrollRight && (
          <button
            onClick={() => scroll('right')}
            className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-background/90 backdrop-blur-sm rounded-full items-center justify-center shadow-lg hover:bg-background transition-all"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        )}
      </div>
    </div>
  );
};

export const BookAvatarSelector = ({ 
  isOpen, 
  onClose, 
  onSelect,
  currentAvatar 
}: BookAvatarSelectorProps) => {
  const { groupedAvatars, isLoading } = useBookAvatars();
  const [selectedAvatar, setSelectedAvatar] = useState<string | null>(currentAvatar || null);
  const isMobile = useIsMobile();

  const handleSelect = (avatarUrl: string) => {
    setSelectedAvatar(avatarUrl);
  };

  const handleConfirm = () => {
    if (selectedAvatar) {
      onSelect(selectedAvatar);
      onClose();
    }
  };

  const content = (
    <>
      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      ) : groupedAvatars.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          No avatars available yet
        </div>
      ) : (
        <ScrollArea className={isMobile ? "h-[60vh]" : "h-[60vh]"}>
          <div className="space-y-6 pb-4">
            {groupedAvatars.map((group) => (
              <AvatarGroup
                key={group.bookTitle}
                group={group}
                selectedAvatar={selectedAvatar}
                onSelect={handleSelect}
              />
            ))}
          </div>
        </ScrollArea>
      )}

      <div className="flex justify-end gap-2 pt-3 border-t border-border/50">
        <Button variant="outline" onClick={onClose} className="px-6 font-apple rounded-xl bg-white/10 backdrop-blur-sm border-white/20">
          Cancel
        </Button>
        <Button 
          onClick={handleConfirm}
          disabled={!selectedAvatar}
          className="px-6 font-apple rounded-xl"
        >
          Confirm
        </Button>
      </div>
    </>
  );

  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={onClose}>
        <DrawerContent className="z-[10000] bg-gray-900/98 backdrop-blur-sm border-t border-white/20">
          <DrawerHeader className="pt-4 pb-2 text-left px-6 md:px-4">
            <DrawerTitle className="text-2xl font-apple text-left px-0">Choose Your Avatar</DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-6">
            {content}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] z-[100] bg-background/95 backdrop-blur-xl border-0">
        <DialogHeader className="pt-4 pb-2 text-left">
          <DialogTitle className="text-2xl font-apple text-left">Choose Your Avatar</DialogTitle>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
};
